import { useMemo } from 'react'
import styles from '../CalculationsBar.module.css'
import { useProjektprognosEditingContext } from '../../context/ProjektprognosEditingContext'
import { useProjectReportComparisonData } from './useProjectReportComparisonData'
import { formatNumber } from '../../../../utils/formatNumber'

export const useProjectReportSummary = () => {
  const { selectedPeriodReport } = useProjektprognosEditingContext()
  const {
    previousProjectAttributes,
    currentProjectAttributes,
    isLoading,
    error,
  } = useProjectReportComparisonData()

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatPercentage = (value: number | null | undefined) => {
    if (value === undefined || value === null) return '-'
    return `${value}%`
  }

  const content = useMemo(() => {
    const results = selectedPeriodReport?.results ?? []
    const details = results[results.length - 1]?.details || []

    return (
      <div
        className={styles.yearSummaryHorizontalContainer}
        style={{ width: `calc(100% - 20px)` }}
      >
        {details.length > 0 && (
          <table className={styles.yearSummaryTable}>
            <thead>
              <tr>
                <th className={styles.fieldsHeaderText}>Årssummering</th>
                {details.map((yearData) => (
                  <th key={yearData.year} className={styles.fieldsHeaderText}>
                    {yearData.year}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={styles.yearSummaryLabel}>
                  Omsättning belopp och år
                </td>
                {details.map((yearData) => (
                  <td key={yearData.year}>
                    {formatNumber(yearData.income) ?? '-'}
                  </td>
                ))}
              </tr>
              <tr>
                <td className={styles.yearSummaryLabel}>
                  Resultat att resultatavräkna belopp och år
                </td>
                {details.map((yearData) => (
                  <td key={yearData.year}>
                    {formatNumber(yearData.result) ?? '-'}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        )}

        <table
          className={styles.yearSummaryTable}
          style={{ marginTop: '20px' }}
        >
          <thead>
            <tr></tr>
            <tr>
              <th className={styles.fieldsHeaderText}></th>
              <th className={styles.fieldsHeaderText}>Föregående prognos</th>
              <th className={styles.fieldsHeaderText}>Nuvarande prognos</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th
                className={styles.fieldsHeader}
                colSpan={3}
                style={{ textAlign: 'left' }}
              >
                Försäljning till BRF
              </th>
            </tr>
            <tr>
              <td className={styles.yearSummaryLabel}>
                Planerad tidpunkt för fastighetsförsäljning
              </td>
              <td>
                {formatDate(
                  previousProjectAttributes?.datesInfo?.saleOfLandPlanned
                )}
              </td>
              <td>
                {formatDate(
                  currentProjectAttributes?.datesInfo?.saleOfLandPlanned
                )}
              </td>
            </tr>
            <tr>
              <th
                className={styles.fieldsHeader}
                colSpan={3}
                style={{ textAlign: 'left' }}
              >
                Planering
              </th>
            </tr>
            <tr>
              <td className={styles.yearSummaryLabel}>
                Planerat igångsättn.medg. FL
              </td>
              <td>
                {formatDate(
                  previousProjectAttributes?.datesInfo
                    ?.mgmtBuildStartConsentPlanned
                )}
              </td>
              <td>
                {formatDate(
                  currentProjectAttributes?.datesInfo
                    ?.mgmtBuildStartConsentPlanned
                )}
              </td>
            </tr>
            <tr>
              <td className={styles.yearSummaryLabel}>Igångsättn.medg. FL</td>
              <td>
                {formatDate(
                  previousProjectAttributes?.datesInfo?.mgmtBuildStartConsent
                )}
              </td>
              <td>
                {formatDate(
                  currentProjectAttributes?.datesInfo?.mgmtBuildStartConsent
                )}
              </td>
            </tr>
            <tr>
              <td className={styles.yearSummaryLabel}>
                Planerad slutbesiktning
              </td>
              <td>
                {formatDate(
                  previousProjectAttributes?.datesInfo?.finalInspectionPlanned
                )}
              </td>
              <td>
                {formatDate(
                  currentProjectAttributes?.datesInfo?.finalInspectionPlanned
                )}
              </td>
            </tr>
            <tr>
              <td className={styles.yearSummaryLabel}>Slutbesiktning</td>
              <td>
                {formatDate(
                  previousProjectAttributes?.datesInfo?.finalInspection
                )}
              </td>
              <td>
                {formatDate(
                  currentProjectAttributes?.datesInfo?.finalInspection
                )}
              </td>
            </tr>
            <tr>
              <th
                className={styles.fieldsHeader}
                colSpan={3}
                style={{ textAlign: 'left' }}
              >
                Ränteberäkning
              </th>
            </tr>
            <tr>
              <td className={styles.yearSummaryLabel}>Ränta kreditiv</td>
              <td>
                {formatPercentage(
                  previousProjectAttributes?.interestInfo
                    ?.buildingCreditInterest
                )}
              </td>
              <td>
                {formatPercentage(
                  currentProjectAttributes?.interestInfo?.buildingCreditInterest
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    )
  }, [
    selectedPeriodReport,
    previousProjectAttributes,
    currentProjectAttributes,
  ])

  return { isLoading, error, content }
}
